import { TFunction } from 'next-i18next'

import { MultipleChoiceQuestion } from '~/types/bootcamp'
import { shuffle } from '~/utils'

import { getInt, getRandomInt } from '~/bootcamp/Activities/Fractions/Fractions.utils'

import { AnswerType, MultipleChoiceActivityData } from './MultipleChoice.type'

/**
 * chooses question and options for the activity
 * @param questions all available multiple choice question
 * @param history previously selected question history
 * @returns multiple choice activity data
 */
export function generateMultipleActivityData(
  questions: MultipleChoiceQuestion[],
  history: number[],
): MultipleChoiceActivityData {
  const forceStopShuffle = process.env.NEXT_PUBLIC_STOP_SHUFFLE === 'true'
  const questionIndex = forceStopShuffle
    ? getInt(0, questions.length - 1, history)
    : getRandomInt(0, questions.length - 1, history)

  const question = JSON.parse(JSON.stringify(questions[questionIndex])) as MultipleChoiceQuestion

  const maximumRightAnswer = Math.max(question.maximumRightAnswer ?? 1, 1) // Maximum right answer should be 1 by default.
  if (!forceStopShuffle && question.shouldShuffle) {
    // shuffle and select options
    const rightOptions = shuffle(question.options.filter((option) => option.isCorrect))
    const wrongOptions = shuffle(question.options.filter((option) => !option.isCorrect))

    let options = [
      ...rightOptions.slice(
        0,
        getRandomInt(Math.max(1, question.showOptions - wrongOptions.length), maximumRightAnswer),
      ),
    ]
    options = [...options, ...wrongOptions.slice(0, question.showOptions - options.length)]
    question.options = shuffle(options)
  } else {
    // select options without shuffle
    let rightIndexes: number[] = []
    // eslint-disable-next-line fp/no-loops
    for (let i = 0; i < question.options.length; i++) {
      if (question.options[i].isCorrect) {
        rightIndexes = [...rightIndexes, i]
      }
    }
    if (rightIndexes.length > 0) {
      // first select a right option randomly
      const rightOptionIndex = rightIndexes[getRandomInt(0, rightIndexes.length - 1)]
      let options = [question.options[rightOptionIndex]]
      // then select the options around the right option to keep the options un-shuffled
      let totalRightAnswer = 1
      let low = rightOptionIndex - 1
      let high = rightOptionIndex + 1
      // eslint-disable-next-line fp/no-loops
      while (options.length < question.showOptions && (high < question.options.length || low >= 0)) {
        if (low >= 0) {
          if (question.options[low].isCorrect && totalRightAnswer < maximumRightAnswer) {
            options = [question.options[low], ...options]
            totalRightAnswer++
          } else if (!question.options[low].isCorrect) {
            options = [question.options[low], ...options]
          }
          low--
        }
        if (high < question.options.length && options.length < question.showOptions) {
          if (question.options[high].isCorrect && totalRightAnswer < maximumRightAnswer) {
            options = [...options, question.options[high]]
            totalRightAnswer++
          } else if (!question.options[high].isCorrect) {
            options = [...options, question.options[high]]
          }
          high++
        }
      }
      question.options = options
    }
  }

  return { questionIndex, question, totalRightAnswer: question.options.filter((option) => option.isCorrect).length }
}

/**
 * check answer status
 * @param question multiple choice question
 * @param answer answer array with boolean value, index matched with mcq index
 * @returns AnswerType(wrong|correct|select-correct|select-all-correct|select-only-correct)
 */
export function checkAnswer(question: MultipleChoiceQuestion, answer: boolean[], totalRightAnswer: number): AnswerType {
  const map = question.options.map((r) => r.isCorrect)
  const totalCorrect = answer.reduce((prevVal, ans, index) => (ans && ans === map[index] ? prevVal + 1 : prevVal), 0)
  const totalIncorrect = answer.reduce((prevVal, ans, index) => (ans && ans !== map[index] ? prevVal + 1 : prevVal), 0)

  if (totalCorrect === 0 && totalIncorrect > 0) {
    return AnswerType.WRONG
  } else if (totalCorrect > 0 && totalCorrect < totalRightAnswer && totalIncorrect > 0) {
    return AnswerType.SELECT_CORRECT
  } else if (totalCorrect > 0 && totalCorrect < totalRightAnswer && totalIncorrect === 0) {
    return AnswerType.SELECT_ALL_CORRECT
  } else if (totalCorrect === totalRightAnswer && totalIncorrect > 0) {
    return AnswerType.SELECT_ONLY_CORRECT
  } else if (totalCorrect === totalRightAnswer && totalIncorrect === 0) {
    return AnswerType.CORRECT
  }
  return AnswerType.WRONG
}

/**
 * Get feedback message from answer type key
 * @param key AnswerType(wrong|correct|select-correct|select-all-correct|select-only-correct)
 * @returns message from selected language option
 */
export const getFeedBackString = (key: string | null, t: TFunction<'bootcamp', undefined>): string => {
  switch (key) {
    case AnswerType.SELECT_ALL_CORRECT:
      return t('feedback.select-all-correct')
    case AnswerType.SELECT_CORRECT:
      return t('feedback.select-correct')
    case AnswerType.SELECT_ONLY_CORRECT:
      return t('feedback.select-only-correct')
    default:
      return ''
  }
}
